import React, {createContext, useContext, useReducer, useEffect, useCallback} from 'react';
import {auth} from '../helpers/firebase';
import {getCurrentUser} from '../pages/User/services/userService';
import {STATS_ROLES, USER_ROLES} from '../pages/User/constants/userRoles';
import Clarity from '@microsoft/clarity';

// Initial state
const initialState = {
  user: null,
  userProfile: null,
  roles: [],
  apps: [],
  loading: true,
  error: null,
  isAuthenticated: false,
  teamName: null
};

// Action types
const ActionTypes = {
  SET_LOADING: 'SET_LOADING',
  SET_USER: 'SET_USER',
  SET_USER_PROFILE: 'SET_USER_PROFILE',
  SET_ERROR: 'SET_ERROR',
  CLEAR_USER: 'CLEAR_USER',
  UPDATE_USER_PROFILE: 'UPDATE_USER_PROFILE'
};

// Reducer
function userRoleReducer(state, action) {
  switch (action.type) {
    case ActionTypes.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };

    case ActionTypes.SET_USER:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        loading: false,
        error: null
      };

    case ActionTypes.SET_USER_PROFILE:
      return {
        ...state,
        userProfile: action.payload,
        roles: action.payload?.roles || [],
        apps: action.payload?.apps || [],
        teamName: action.payload?.teamName || null,
        loading: false,
        error: null
      };

    case ActionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false
      };

    case ActionTypes.CLEAR_USER:
      return {
        ...initialState,
        loading: false
      };

    case ActionTypes.UPDATE_USER_PROFILE:
      return {
        ...state,
        userProfile: {
          ...state.userProfile,
          ...action.payload
        },
        roles: action.payload.roles || state.roles,
        apps: action.payload.apps || state.apps,
        teamName: action.payload.teamName || state.teamName
      };

    default:
      return state;
  }
}

// Create context
const UserRoleContext = createContext();

// Provider component
export function UserRoleProvider({children}) {
  const [state, dispatch] = useReducer(userRoleReducer, initialState);

  // Fetch user profile from our API
  const fetchUserProfile = useCallback(async () => {
    if (!state.user) return;

    try {
      dispatch({type: ActionTypes.SET_LOADING, payload: true});
      const response = await getCurrentUser();

      if (response.success) {
        dispatch({type: ActionTypes.SET_USER_PROFILE, payload: response.data});
      } else {
        dispatch({
          type: ActionTypes.SET_ERROR,
          payload: response.error || 'Failed to fetch user profile'
        });
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      dispatch({type: ActionTypes.SET_ERROR, payload: error.message});
    }
  }, [state.user]);

  // Update user profile
  const updateUserProfile = useCallback(updates => {
    dispatch({type: ActionTypes.UPDATE_USER_PROFILE, payload: updates});
  }, []);

  // Refresh user profile
  const refreshUserProfile = useCallback(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);

  // Firebase auth state listener
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      if (user) {
        dispatch({type: ActionTypes.SET_USER, payload: user});
      } else {
        dispatch({type: ActionTypes.CLEAR_USER});
      }
    });

    return unsubscribe;
  }, []);

  // Fetch user profile when user changes
  useEffect(() => {
    if (state.user && state.isAuthenticated) {
      fetchUserProfile();
    }
  }, [state.user, state.isAuthenticated, fetchUserProfile]);

  useEffect(() => {
    if (state?.user?.email) {
      Clarity.setTag('email', state.user.email);
    }
  }, [state?.user?.email]);

  const value = {
    // State
    ...state,

    // Actions
    updateUserProfile,
    refreshUserProfile,

    // Computed values
    isAdmin:
      state.user?.email &&
      [
        'anhnt@avada.io',
        'sam@avada.io',
        'anhth@avada.io',
        'lamln@avadagroup.com',
        'nghiavt@avadagroup.com',
        'nghiavt@avada.email',
        'anhth01@avadagroup.com'
      ].includes(state.user.email),
    hasRole: role => state.roles.includes(role),
    hasAnyRole: roles => roles.some(role => state.roles.includes(role)),
    hasAllRoles: roles => roles.every(role => state.roles.includes(role)),
    canAccessApp: appId => state.apps.some(app => app.appId === appId),
    getUserApps: () => state.apps,
    getTeamName: () => state.teamName,
    getUserEmail: () => state.user.email
  };

  return <UserRoleContext.Provider value={value}>{children}</UserRoleContext.Provider>;
}

// Hook to use the context
export function useUserRole() {
  const context = useContext(UserRoleContext);
  if (!context) {
    throw new Error('useUserRole must be used within a UserRoleProvider');
  }
  return context;
}

// Convenience hooks for common use cases
export function useUserRoles() {
  const {roles} = useUserRole();
  return roles;
}

export function useUserApps() {
  const {apps} = useUserRole();
  return apps;
}

export function useIsAdmin() {
  const {isAdmin} = useUserRole();
  return isAdmin;
}

export function useUserTeamName() {
  const {teamName} = useUserRole();
  return teamName;
}

export function useHasRole(role) {
  const {hasRole} = useUserRole();
  return hasRole(role);
}

export function useHasAnyRole(roles) {
  const {hasAnyRole} = useUserRole();
  return hasAnyRole(roles);
}

export function useHasRoles(roles) {
  const {hasAllRoles} = useUserRole();
  return hasAllRoles(roles);
}

export function useCanAccessApp(appId) {
  const {canAccessApp} = useUserRole();
  return canAccessApp(appId);
}

export function useTeamName() {
  const {getTeamName} = useUserRole();
  return getTeamName();
}

// Role-based permission hooks
export function useCanViewStats() {
  return useHasRole(STATS_ROLES.VIEW_STATS);
}

export function useCanManageApps() {
  return useHasRoles([USER_ROLES.ADD_APP, USER_ROLES.EDIT_APP, USER_ROLES.DELETE_APP]);
}

export function useCanManageUsers() {
  return useHasRoles([USER_ROLES.ADD_USER, USER_ROLES.EDIT_USER, USER_ROLES.DELETE_USER]);
}
